/** Retailer & distributor-friendly feature content for the Zuntly company site.
 *  Plain language - no tech jargon.
 */

export const platformSurfaces = [
  {
    id: 'admin',
    label: '01',
    name: 'Your Business Control Panel',
    product: 'For owners & store managers',
    tagline: 'One place to run your entire retail business.',
    description:
      'From your laptop or desktop, manage products, stock, orders, offers, billing, delivery, and reports. Whether you have one shop or many branches, everything stays in sync.',
    notes: [
      'Owner can see every branch; store managers only see what they need',
      'Turn online store, offline billing, or Free Cash wallet on/off as you grow',
      'Watch any branch’s live orders without logging into that store separately',
      'Staff can work from multiple phones/laptops - force logout if someone leaves',
      'Hide revenue numbers from juniors who should only handle packing',
      'Decide who can edit products, approve stock, or send offers',
      'Manage your shop domains, GST details, and subscription in one place',
      'Hear a clear alert sound the moment a new order arrives',
      'Find the best locations for new stores with map-based intelligence',
      'Download bills, stock sheets, and reports as PDF or Excel',
      'Reset passwords safely if a manager forgets login',
      'Keep your business profile and branding up to date',
    ],
  },
  {
    id: 'customer',
    label: '02',
    name: 'Your Branded Customer App',
    product: 'For your shoppers',
    tagline: 'Looks like your brand → not a generic marketplace.',
    description:
      'Customers shop in an app that carries your name, colours, and offers. They browse, order, track delivery, use wallet cash, and return items - all under your brand.',
    notes: [
      'Customers open your app / link and land in your store only',
      'Login with mobile OTP or Google - simple and familiar',
      'Ask for name before checkout so delivery labels are correct',
      'Guests can browse freely; login only when they want to buy or save favourites',
      'Force app update when you release important improvements',
      'One click to paint the whole app in your brand colour',
      'Choose from 12+ bottom-menu styles to match your brand personality',
      'Cart badge shows how many items they added - hard to miss',
      'Push messages open the right product, offer, or order screen',
      'See which products customers view most so you stock smarter',
      'Show product videos and GST invoices inside the shopping journey',
    ],
  },
  {
    id: 'delivery',
    label: '03',
    name: 'Delivery Partner App',
    product: 'For drivers & branch desks',
    tagline: 'From pickup to doorstep every step tracked and confirmed.',
    description:
      'Your riders see nearby orders, navigate to the shop, pick up, deliver with OTP or collect COD, and handle return pickups. Branch desks assign riders in seconds.',
    notes: [
      'Drivers mark themselves available when they start the shift',
      'Live location helps you see who is closest to the order',
      'Riders can accept or skip a nearby delivery request',
      'Clear steps: Pickup → Out for delivery → Delivered',
      'OTP confirmation for prepaid orders - no fake “delivered” marks',
      'COD collection recorded when cash is taken from the customer',
      'Return pickups when a product needs to come back for quality check',
      'Branch desk sees waiting orders and assigns the right rider',
      'Push alerts so drivers never miss a new assignment',
    ],
  },
  {
    id: 'backbone',
    label: '04',
    name: 'Always-On Business Backbone',
    product: 'Works behind the scenes',
    tagline: 'Keeps every shop, app, and delivery in sync 24/7.',
    description:
      'The system that connects your control panel, customer app, and delivery app. Stock updates instantly, payments settle cleanly, and reminders go out without you chasing anyone.',
    notes: [
      'Each retailer gets a private space - your data never mixes with another brand',
      'Clear order IDs so warehouse and accounts can reconcile easily',
      'Wallet cash expires in the right order so accounting stays clean',
      'Automatic reminders before Free Cash expires for customers',
      'Abandoned carts cleaned up so reports stay meaningful',
      'Monthly backups of walk-in sales, stock moves, and delivery logs',
      'Courier partner tracking updates flow into the customer’s order screen',
      'Optional multi-brand marketplace if you sell to other retailers too',
      'QR / walk-in catalogue so counter customers buy from the same stock',
      'Role checks so juniors cannot change owner-only settings',
    ],
  },
];

export const featureAtlas = [
  {
    id: 'your-business',
    tab: 'One Chain, Full Control',
    title: 'Your business, your branches, your rules',
    summary:
      'Run one shop or a chain. Each branch keeps its own stock and staff, while you stay in control from the top.',
    detail:
      'If you are a distributor or multi-outlet retailer, Zuntly treats your head office as the “master” and every outlet as a branch. You decide who can see sales, who can move stock, and which branch serves which pincode.',
    notes: [
      'Separate login space for your brand - other retailers cannot see your data',
      'Add as many branches as you grow (city stores, dark stores, warehouses)',
      'Owner can “peek” into any branch’s live orders without switching accounts',
      'Staff can stay logged in on phone + laptop; remove access when they leave',
      'Limit how many products a starter plan can list; unlock more as you upgrade',
      'Owner / manager / packing staff - different permissions for each',
      'Switch online selling, counter billing, or wallet rewards on when you are ready',
    ],
  },
  {
    id: 'catalog',
    tab: 'List Once, Sell Everywhere',
    title: 'Products that match how you actually sell',
    summary:
      'List grocery by weight, electronics by RAM/colour, fashion by size - with GST, barcodes, and expiry built in.',
    detail:
      'Stop forcing every product into one “simple SKU”. Zuntly understands real retail: same rice in 1kg / 5kg, same phone in different storage, add-ons like extended warranty, and expiry dates for FMCG.',
    notes: [
      'Sell the same item online and at the counter from one catalogue',
      'Price by packet, kg, litre, or piece - whatever your category needs',
      'Variants like colour, size, RAM, or pack size with their own price & photo',
      'Set expiry date per pack - staff see red/yellow warnings before stock goes bad',
      'Add highlights and detail tabs (like big e-commerce sites) so customers trust the listing',
      'Enter GST / HSN so invoices stay compliant',
      'Link the vendor who supplies each product for purchase planning',
      'Scan barcode at the counter to find the product instantly',
      'Connect related products (“customers also buy…”) and paid add-ons',
      'Generate a clear product description with AI when you are short on time',
      'Boost star ratings carefully when you have genuine happy customers to showcase',
      'Mark items inactive or out of stock without deleting history',
    ],
  },
  {
    id: 'inventory',
    tab: 'Stock You Can Trust',
    title: 'Stock that moves with approval - not chaos',
    summary:
      'Warehouse sends stock to branches. Branches request more. Every movement is logged.',
    detail:
      'Distributors and multi-branch retailers lose money when stock is informal. Zuntly makes transfers visible: approve, dispatch, receive, and audit. Customers only see what their delivery zone can actually fulfil.',
    notes: [
      'Each branch keeps its own shelf quantity',
      'Shared pool for items sold by weight/metric when that fits your model',
      'Warehouse can push bulk stock; branches accept after checking',
      'Branches can raise a stock request instead of calling the warehouse',
      'Full movement history - who moved what, and when',
      'Pincode / zone stock so customers do not order what you cannot deliver there',
      'Track sold online vs sold at counter separately',
      'Vendors list + purchase orders with PDF/Excel for accounts',
      'Spot “dead” products that never move so you stop over-buying',
      'Low / no stock alerts on email or WhatsApp before you go empty',
    ],
  },
  {
    id: 'orders',
    tab: 'Every Order, One Screen',
    title: 'Orders from app, counter, and phone - one pipeline',
    summary:
      'Online, walk-in, and staff-created orders follow the same confirm → pack → ship → deliver journey.',
    detail:
      'Whether the customer ordered on the app, walked into the shop, or called your counter, your team works from one order list. Split carts across branches when items sit in different stores.',
    notes: [
      'Online orders with clear IDs for warehouse and accounts',
      'Walk-in billing at the counter using the same stock',
      'Create an order for a customer who called the shop',
      'Live order list with sound when something new arrives',
      'Change status step by step: confirm, pack, ship, deliver',
      'Move an order to another branch if that store has stock',
      'Cancel one item without cancelling the whole bag',
      'Auto-confirm online orders if you trust high-volume days',
      'One checkout can pull items from multiple branches',
      'UPI / cards / wallets online, or Cash on Delivery',
      'GST invoice the customer can open after purchase',
      'Live map tracking so customers stop calling “Where is my order?”',
      'Expiry of the packed item remembered on the order line',
      'Delivery desk and courier settings managed from the same panel',
    ],
  },
  {
    id: 'post-order',
    tab: 'Returns & Happy Customers',
    title: 'After delivery - returns, refunds, unhappy reviews',
    summary:
      'A work desk for exceptions so one bad order does not become a forgotten complaint.',
    detail:
      'Retailers lose trust when returns are messy. Zuntly tracks the return request, pickup, quality check, and refund method. A 1-star review can open a ticket with the order attached so you fix it fast.',
    notes: [
      'Timeline view of what happened after the order was placed',
      'Customer can request a return from the app',
      'Driver picks up the return for quality check',
      'Refund to original payment, store credit, or bank - your policy',
      'Set return windows and rules once; staff follow them',
      '1-star reviews create a ticket with order context for your team',
      'Resolve tickets and optionally boost genuine ratings carefully',
      'Ratings tools for monitoring product feedback',
    ],
  },
  {
    id: 'pos',
    tab: 'Counter Billing That Matches',
    title: 'Counter billing that shares the same stock',
    summary:
      'Scan, bill, and print - walk-in sales update the same inventory your app uses.',
    detail:
      'No more separate “offline software” that drifts from online stock. Your counter, QR store, and app sell from one truth. Great for kirana, electronics, pharmacy, and wholesale counters.',
    notes: [
      'Turn counter billing on only when your shop needs it',
      'Standard and premium billing modes for different shop sizes',
      'QR and barcode scanning at the till',
      'Choose bill print templates that match your stationery',
      'Walk-in sales reports with PDF for the day / month',
      'Accept cash, card, or UPI at the counter',
      'QR catalogue for customers who scan and self-pick in store',
      'Print or share receipts instantly',
      'Month-end backup of walk-in sales for accounts',
    ],
  },
  {
    id: 'promotions',
    tab: 'Offers, Deals & Free Cash',
    title: 'Offers, flash deals & store wallet cash',
    summary:
      'Coupons, timed deals, bulk discounts, and Free Cash that brings customers back.',
    detail:
      'Run festival offers without Excel chaos. Give store credit (“Free Cash”) that expires, reward delivery, and apply at checkout. Set a minimum cart value so small loss-making orders do not eat delivery cost.',
    notes: [
      'Coupons as % off or flat ₹ off - limit to categories or products',
      'Timer deals that end automatically when the clock runs out',
      'Slide-up deal screens on the app so offers are hard to miss',
      'Flash discounts on a single hero product for the homepage',
      'Buy more, save more - min quantity discounts per pack',
      'Free Cash wallet customers can see and spend',
      'Oldest wallet credit is used first (fair expiry)',
      'Auto-reward wallet cash after a successful delivery',
      'Load Free Cash to a segment (VIPs, inactive buyers, festival list)',
      'Apply wallet + coupon together at checkout with clear rules',
      'Remind customers before wallet cash expires',
      'See how Free Cash is driving repeat purchases',
      'Minimum order value and cart limits to protect margins',
    ],
  },
  {
    id: 'design',
    tab: 'Make It Look Like Yours',
    title: 'Make the store look like your brand',
    summary:
      'Banners, categories, colours, footer, and menu style - without hiring a designer every week.',
    detail:
      'Your app should feel like your shop board, not a borrowed template. Change theme colour, homepage sections, festival banners, and navigation style whenever a campaign starts.',
    notes: [
      'Homepage showcase sections and photo gallery',
      'Ordered banners for festivals and launches',
      'Image links that jump to categories or products',
      'Footer with your policies and contacts',
      'One brand colour across buttons and highlights',
      'Sticky category row and subcategories for easy browsing',
      'Hide delivery timeline on PDP if you prefer a cleaner page',
      'Show or hide timer/flash deals on the home screen',
      'Search suggestions as customers type',
      'Store visibility on maps when you want local discovery',
      '12 menu styles - from minimal to bold - matched to your brand',
    ],
  },
  {
    id: 'customer-ux',
    tab: 'From Browse to Buy',
    title: 'What your customer experiences (step by step)',
    summary:
      'Browse → search → add → pay → track → rate → return. Designed for Indian shopping habits.',
    detail:
      'Explain this to your staff: the customer journey is complete. Guests look around freely. Checkout asks for address, checks if you deliver to that area, applies coupon/wallet, and takes UPI or COD.',
    notes: [
      'Splash opens quickly with their last login remembered',
      'Home shows your banners, categories, and deals',
      'Product page: variants, add-ons, “can we deliver?”, ratings, video',
      'Open products from category, banner, or homepage section',
      'Smart search when they do not know the exact name',
      'Read all reviews before buying',
      'Save favourites for later',
      'Buy Again for staples they reorder every month',
      'Checkout checks stock, nearest branch, delivery slot/tier, coupon, wallet',
      'Quick Buy for repeat items; normal cart for browsing',
      'One bag can be fulfilled by more than one branch',
      'Order history with live map and cancel options',
      'Success screen with GST invoice',
      'Return request without calling the shop',
      'Profile and addresses for next time',
      'Wallet screen with balance and history',
      'Notification centre for offers and order updates',
      'OTP / Google login popups - no confusing full-page redirects',
      'Rate single items or the whole order',
      'Timer deals pop up during festivals',
      'App asks to update when you publish a must-have fix',
    ],
  },
  {
    id: 'fulfillment',
    tab: 'Riders & Couriers',
    title: 'Your riders + courier partners - together',
    summary:
      'Use your own fleet for nearby orders and a courier partner for longer distance - in one flow.',
    detail:
      'Hyperlocal shops love own riders with OTP. Pan-city or heavy shipments can go through a courier partner with tracking. Customers still see one clear status story.',
    notes: [
      'Drivers share live location during the shift',
      'Nearby order broadcast - accept if they can take it',
      'Pickup → out for delivery → OTP or COD complete',
      'Return pickups for failed or quality returns',
      'Branch desk assigns the right rider to waiting orders',
      'Screens for driver home, order detail, and return detail',
      'Branch order queue and returns desk',
      'Courier partner for longer routes and hyperlocal modes',
      'Tracking updates appear on the customer’s order',
      'Delivery area and fee tiers decided at checkout',
      'ETA based on what that zone can actually supply',
      'Mix own fleet and courier based on distance',
      'Keep logs of who delivered what for disputes',
    ],
  },
  {
    id: 'notifications',
    tab: 'Push, SMS & WhatsApp',
    title: 'Reach customers where they already look',
    summary:
      'App push, SMS, email, and WhatsApp - with messages that open the right screen.',
    detail:
      'Do not send a vague “check our app”. Send a push that opens the exact product or order. Automate status SMS so packing staff do not type updates manually.',
    notes: [
      'Loud order bell on the control panel when sales come in',
      'Reusable message templates with customer name / order id filled in',
      'Send campaigns to a filtered list (inactive, VIPs, city-wise)',
      'Push opens product, banner, category, or orders screen',
      'Automatic messages when order status changes',
      'In-app notification list for customers who muted SMS',
      'Alerts for drivers and branch desks',
      'OTP and status SMS',
      'Email for invoices and longer updates',
      'WhatsApp for urgent stock / order alerts to your team',
    ],
  },
  {
    id: 'analytics',
    tab: 'Know What Sold Today',
    title: 'Numbers that help you buy and sell better',
    summary:
      'See who’s shopping, which carts were left, and what to do next - today, not at month-end.',
    detail:
      'Open Customers → Micro Analysis. Simple screens for live shoppers, left carts, weak products, Free Cash, and counter sales.',
    notes: [
      'Who is in the app right now (last 2 minutes)',
      'Today, Yesterday, or last 7 / 15 / 30 days',
      'Views, carts, orders, and cancels in one look',
      'Busy hours and quiet hours',
      'Left carts - who to call or offer first',
      'People who browsed a lot but didn’t buy',
      'Star products vs slow / dead stock',
      'Compare branches side by side',
      'Free Cash still unused or expiring soon',
      'Walk-in counter busy hours and fast sellers',
      'A short “do this next” list for your team',
      'Export sales and stock sheets for accounts',
    ],
  },
  {
    id: 'org',
    tab: 'Head Office Power',
    title: 'Head office tools for growing chains',
    summary:
      'Domains, GST, permissions, marketplace, and subscriptions - set once as you scale.',
    detail:
      'When you add the 5th branch, chaos starts unless head office tools are ready. Zuntly gives you branch creation, tax masters, and access control before that pain hits.',
    notes: [
      'Create master warehouse and retail branches',
      'Warehouse mode for distribution-first businesses',
      'Connect your own website / app domain to your store',
      'Maintain GST settings for compliant billing',
      'Track business metrics across the network',
      'Fine-grained staff permissions',
      'Optional marketplace to sell to other businesses',
      'Manage your Zuntly plan from the same panel',
      'Keep company profile current for customers and staff',
    ],
  },
  {
    id: 'marketplace',
    tab: 'Sell to Businesses',
    title: 'Sell to other businesses too',
    summary:
      'Optional multi-store marketplace and web shopfront when you outgrow a single brand app.',
    detail:
      'Distributors who also want a catalogue for retailers, or groups running many brands, can open a marketplace layer and a website shopfront on top of the same operations.',
    notes: [
      'Other businesses can browse and order from your catalogue',
      'Login, store list, cart, and orders for marketplace buyers',
      'Share master products into the marketplace cleanly',
      'Admin view for marketplace orders',
      'Dedicated marketplace shopping experience',
      'Website storefront for customers who prefer browser over app',
    ],
  },
  {
    id: 'trust',
    tab: 'Lock Who Can Edit',
    title: 'Safe access for owners, staff & customers',
    summary:
      'Only the right people change prices, see revenue, or manage deliveries.',
    detail:
      'Retail theft of access is common - ex-staff with old passwords, juniors editing MRP. Zuntly locks actions by role and keeps each retailer’s world private.',
    notes: [
      'Secure login for owners and staff',
      'Owner-only actions blocked for junior roles',
      'Spectate mode for owners reviewing branches',
      'Support staff access without full owner powers',
      'Customer always lands in the correct brand store',
      'Delivery staff see only delivery tasks',
      'Strong password storage practices',
      'Courier tracking updates verified before they change order status',
      'OTP and Google login for customers',
      'Forgot password flow for managers',
    ],
  },
];

export const aiSuite = [
  {
    id: 'brain',
    name: 'Zuntly Brain',
    role: 'Ask your business in simple words',
    description:
      'Type questions like “which items expire this week?” or “move stock tips” - get answers from your live shop data instead of digging through menus.',
    notes: [
      'Chat history so you can continue yesterday’s questions',
      'Inventory help in everyday language',
      'Expiry-aware answers for FMCG and pharma-style stock',
      'Uses your real store data - not generic internet advice',
      'Easy to find in the control panel menu',
    ],
  },
  {
    id: 'revenue',
    name: 'Revenue & Buying Assistant',
    role: 'What to stock before demand hits',
    description:
      'Get suggestions for festival demand, missing bestsellers, and profitable picks - download a PDF for your next purchase meeting with vendors.',
    notes: [
      'Festival and seasonal stocking ideas',
      'Spot products customers search but you do not carry',
      'Highlight high-margin opportunities',
      'Weather-aware demand hints for relevant categories',
      'PDF you can share with purchase managers',
      'Richer AI on higher plans',
    ],
  },
  {
    id: 'geoiq',
    name: 'GeoIQ - Where to open next',
    role: 'Store expansion without guessing',
    description:
      'Compare neighbourhoods, competitors, and catchments on a map. Model “what if we open here?” before you sign a lease.',
    notes: [
      'Map search for candidate localities',
      'Draw the area you want to serve',
      'See how crowded the competition already is',
      'Estimate return on a new outlet',
      'Try different “what if” scenarios',
      'Plan multi-city expansion with the same method',
    ],
  },
  {
    id: 'cinematic',
    name: 'Cinematic Studio',
    role: 'Turn product photos into short ads',
    description:
      'Upload photos, get a script and voice, and export a short video for WhatsApp status, Reels, or festival campaigns - without a film crew.',
    notes: [
      'Scripts written from your product context',
      'Voiceover for the ad',
      'Export ready-to-post video',
      'Styles for wedding, travel, cinematic looks, and more',
      'Built for fast festival and social pushes',
    ],
  },
];

/** Customers → Micro Analysis - short, shop-owner language */
export const microAnalysisSuite = [
  {
    id: 'live-pulse',
    name: 'Who’s in the app now',
    role: 'Live view',
    description: 'See shoppers using your app in the last 2 minutes - and what they are doing.',
    notes: [
      'Pick Today, Yesterday, or last 7 / 15 / 30 days',
      'How many are browsing right now',
      'Views, cart adds, orders, and cancels in one look',
      'Copy a customer’s mobile to call them',
    ],
  },
  {
    id: 'recovery-os',
    name: 'Save left carts',
    role: 'Get the sale back',
    description: 'Find people who almost bought - and tell your team who to call or offer first.',
    notes: [
      'Carts left for 30 minutes to 12+ hours',
      'Urgent ones marked Critical / High',
      'See what’s in the cart and its value',
      'Clear next step: push, coupon, or call',
    ],
  },
  {
    id: 'product-iq',
    name: 'Which products work',
    role: 'Stock that sells',
    description: 'Know which items are stars, which get stuck in cart, and which never sell.',
    notes: [
      'Tags: Star, Rising, or At Risk',
      'Score out of 100 with a simple tip',
      'Items with stock but zero sales',
      'Items people view but don’t buy',
    ],
  },
  {
    id: 'time-place',
    name: 'Busy hours & areas',
    role: 'When & where',
    description: 'See your busiest hours, quiet hours, and which pincodes order most.',
    notes: [
      'Peak shopping hour of the day',
      'Busy vs quiet weekdays',
      'Hot / Warm / Cold customers',
      'Top and slow pincodes for delivery',
    ],
  },
  {
    id: 'branch-playbook',
    name: 'Branch vs branch',
    role: 'Chain view',
    description: 'Compare stores fairly - who sells more, and what the weaker branch should fix.',
    notes: [
      'Sales and orders by branch',
      'Which store is ahead',
      'Weak products in one branch',
      'Simple “do this” tips vs your best store',
    ],
  },
  {
    id: 'free-cash-iq',
    name: 'Free Cash wallet',
    role: 'Store credit',
    description: 'See how much wallet cash is still unused - and who to remind before it expires.',
    notes: [
      'How much Free Cash is still pending',
      'Who used it vs who never used it',
      'Balances expiring soon',
      'Tips to get them to spend it',
    ],
  },
  {
    id: 'pos-micro',
    name: 'Counter billing insights',
    role: 'Walk-in sales',
    description: 'Same clear view for walk-in bills - busy hours, fast sellers, and what to reorder.',
    notes: [
      'Best hour and best day at the till',
      'Fast-moving vs slow items',
      'Stock that never sells at counter',
      'Items often bought together',
    ],
  },
  {
    id: 'ai-signals',
    name: 'What to do next',
    role: 'Team checklist',
    description: 'A short list for staff: recover this cart, win back this customer, fix this product.',
    notes: [
      'Likely orders in the next hour',
      'Customers who may leave soon',
      'Priority 1, 2, 3 actions for the day',
      'Who to offer a deal to first',
    ],
  },
];

export const omnichannelSteps = [
  {
    step: '01',
    title: 'Dress the store',
    body: 'Set your colour, banners, categories, and app menu. Customers should recognise your brand in three seconds.',
    notes: ['Brand colour', 'Festival banners', 'Homepage sections', 'Menu style'],
  },
  {
    step: '02',
    title: 'Fill every shelf',
    body: 'Warehouse sends stock with approval. Each area only sells what it can deliver.',
    notes: ['Branch stock', 'Approvals', 'Expiry watch', 'Vendor POs'],
  },
  {
    step: '03',
    title: 'Sell everywhere',
    body: 'App, website, QR in-shop, and counter billing share one catalogue and one stock truth.',
    notes: ['Branded app', 'Counter billing', 'Multi-branch cart', 'UPI & COD'],
  },
  {
    step: '04',
    title: 'Deliver & recover',
    body: 'Own riders or courier. Returns, refunds, and bad reviews handled in one place - not forgotten WhatsApp chats.',
    notes: ['OTP delivery', 'Courier tracking', 'Returns desk', '1-star tickets'],
  },
];

/** Replaces tech stack - business outcomes retailers care about */
export const businessOutcomes = [
  {
    title: 'Stop losing sales to “out of stock” surprises',
    desc: 'Online and counter share the same stock. Zone rules stop customers ordering what you cannot deliver to their area.',
  },
  {
    title: 'Run many branches without losing control',
    desc: 'Head office sees everything. Branch staff only see their store. Transfers need approval - no informal stock leakage.',
  },
  {
    title: 'Cut “Where is my order?” calls',
    desc: 'Customers track on a live map. Riders confirm with OTP. Status messages go out automatically.',
  },
  {
    title: 'Bring customers back with wallet cash',
    desc: 'Reward deliveries with Free Cash, set expiry, and let them spend it on the next order - loyalty without plastic cards.',
  },
  {
    title: 'Festival offers without Excel chaos',
    desc: 'Timer deals, flash products, coupons, and banners - switch campaigns on and off from one panel.',
  },
  {
    title: 'Grow with AI that understands retail',
    desc: 'Ask what expires soon, what to buy before Diwali, where to open next, or turn photos into short ads.',
  },
];

export const whoItsFor = [
  'Kirana & supermarket chains',
  'Electronics & mobile retailers',
  'FMCG distributors',
  'Pharmacy & wellness stores',
  'Fashion & lifestyle multi-brand',
  'Hyperlocal delivery brands',
  'Wholesale → retail networks',
  'Regional D2C brands going multi-city',
];

export const completeFeatureChecklist = [
  'Private space for your brand only',
  'One shop or many branches',
  'Owner can watch any branch live',
  'Staff access you can revoke',
  'Different permissions by role',
  'Online + counter from one catalogue',
  'Weight / pack / variant pricing',
  'Barcode at the till',
  'Expiry warnings before stock spoils',
  'GST-ready invoices',
  'Vendor-linked products',
  'Related products & add-ons',
  'AI product write-ups',
  'Branch-wise stock',
  'Warehouse → branch approvals',
  'Stock request from outlets',
  'Full stock movement history',
  'Zone-wise deliverability',
  'Purchase orders for vendors',
  'Dead stock visibility',
  'Low stock alerts',
  'App orders',
  'Walk-in / counter orders',
  'Phone orders created by staff',
  'Multi-branch checkout',
  'Confirm → pack → ship → deliver',
  'Transfer order between stores',
  'Cancel a single item',
  'UPI / cards / COD',
  'GST invoice for customers',
  'Live delivery map',
  'Returns with quality pickup',
  'Refunds your way',
  '1-star review tickets',
  'Counter QR & barcode billing',
  'Printable bill templates',
  'Walk-in sales reports',
  'Coupons & scoped offers',
  'Timer & flash deals',
  'Bulk “buy more save more”',
  'Free Cash wallet',
  'Wallet reward after delivery',
  'Minimum order value',
  'Brand colour & banners',
  '12 app menu looks',
  'Homepage sections & gallery',
  'Guest browse + easy login',
  'Smart search',
  'Favourites & Buy Again',
  'OTP delivery by your riders',
  'Courier partner for longer routes',
  'Branch desk assigns riders',
  'Sales & branch dashboards',
  'Abandoned cart follow-ups',
  'Push / SMS / WhatsApp updates',
  'Zuntly Brain Q&A',
  'Buying & festival suggestions',
  'New store location intelligence',
  'Photo → short ad videos',
  'Website shopfront option',
  'Who’s in the app now',
  'See live shoppers (last 2 minutes)',
  'Busy-hour shopping view',
  'Save left carts before they go cold',
  'People who browsed but didn’t buy',
  'Hot / Warm / Cold customers',
  'Star vs slow products',
  'Dead stock with no sales',
  'Where money is still stuck',
  '“Do this next” list for staff',
  'Likely orders in the next hour',
  'Compare branch sales',
  'Free Cash still unused',
  'Counter busy hours & fast sellers',
  'Items often bought together',
  'One customer’s full shopping path',
];

export const subscriptionPlans = [
  {
    name: 'Basic Plan',
    featured: false,
    prices: {
      monthly: { price: '₹2,699/month', note: 'Billed for 1 month' },
      semiAnnual: { price: '₹2,160/month', note: 'Billed for 6 months', discount: '20% off' },
    },
    features: [
      '20 Products',
      'Basic sales view',
      '1 user login',
      'Secure storefront',
      'Mobile-friendly shop look',
      'Simple checkout',
      'Cash on Delivery',
      'Order updates on SMS & Email',
      'Inventory included',
      '3 theme layouts',
      '1 photo per product',
      'Up to 4 gallery images',
      'Your brand colour in one click',
      'Up to 2 coupons',
      'Festival banners & footer',
      '24/7 support + onboarding help',
    ],
  },
  {
    name: 'Standard Plan',
    featured: true,
    prices: {
      monthly: { price: '₹8,599/month', note: 'Billed for 1 month' },
      semiAnnual: { price: '₹6,879/month', note: 'Billed for 6 months', discount: '20% off' },
    },
    features: [
      'Unlimited products',
      'Deeper sales analytics',
      '3 staff logins',
      'Customisable themes',
      'Abandoned cart recovery',
      'COD + online payments',
      'Print & download invoices',
      'Low stock email alerts',
      '10 theme layouts',
      'Up to 3 photos per product',
      'Up to 8 gallery images',
      'Brand colour in one click',
      'Up to 10 coupons',
      'Festival banners & footer',
      'Basic AI product/coupon help',
      '24/7 support + onboarding help',
    ],
  },
  {
    name: 'Premium Plan',
    featured: false,
    prices: {
      monthly: { price: '₹24,999/month', note: 'Billed for 1 month' },
      semiAnnual: { price: '₹19,999/month', note: 'Billed for 6 months', discount: '20% off' },
    },
    features: [
      'Unlimited products',
      'Premium live insights',
      '10 staff logins',
      'Richer themes',
      'Abandoned cart email campaigns',
      'COD + online payments',
      'SMS, Email & WhatsApp updates',
      'Auto-route to nearest branch',
      'WhatsApp low-stock alerts',
      '20 theme layouts',
      'Up to 5 photos per product',
      'Up to 20 gallery images',
      'Unlimited coupons',
      'Personalised AI recommendations',
      'Store management mobile app',
      'Priority support + account manager',
    ],
  },
  {
    name: 'Custom Plan',
    featured: false,
    prices: {
      monthly: { price: 'Custom Pricing', note: '' },
      semiAnnual: { price: 'Custom Pricing', note: '' },
    },
    features: [
      'Unlimited products',
      'Enterprise reporting',
      'Unlimited users with roles',
      'Dedicated hosting',
      'Multi-language & multi-currency',
      'Advanced marketing (Email, SMS, Push)',
      'Custom dashboards',
      'ERP / CRM connections on request',
      'Team training & onboarding',
      '24/7 priority support with SLA',
      'Dedicated account manager',
    ],
  },
];

export const navigation = [
  { name: 'Home', href: 'home', path: '/' },
  { name: 'Platform', href: 'platform', path: '/platform' },
  { name: 'Features', href: 'features', path: '/features' },
  { name: 'AI', href: 'ai', path: '/solutions' },
  { name: 'Why Zuntly', href: 'why', path: '/technology' },
  { name: 'Plans', href: 'subscriptions', path: '/subscriptions' },
  { name: 'Contact', href: 'contact', path: '/contact' },
];
